import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { Send, Trash2, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, streamPost } from "@/lib/api";
import { PageHead } from "@/components/shop/common";

const SUGGESTIONS = [
  "Which jobs are waiting on parts?",
  "How much have we invoiced this month?",
  "What parts are low and need reordering?",
  "What's the usual cause of a DPF regen failure on a heavy truck?",
];

export default function Assistant() {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);

  useEffect(() => { api.get("/ai/chat").then((r) => setMsgs(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (m) => {
    const message = (m ?? text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((x) => [...x, { role: "user", content: message }, { role: "assistant", content: "" }]);
    const append = (c) => setMsgs((x) => [...x.slice(0, -1), { ...x[x.length - 1], content: x[x.length - 1].content + c }]);
    try {
      await streamPost("/ai/chat", { message }, (ev) => {
        if (ev.type === "text") append(ev.content);
        if (ev.type === "error") append(`\n[Error: ${ev.message}]`);
      });
    } catch (e) { append(`[Error: ${e.message}]`); } finally { setBusy(false); }
  };

  const clear = async () => { await api.delete("/ai/chat"); setMsgs([]); };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      <PageHead eyebrow="AI" title="Shop assistant">
        {msgs.length > 0 && <Button variant="ghost" onClick={clear} className="text-muted-foreground hover:text-red-400" data-testid="chat-clear-btn"><Trash2 className="h-4 w-4 mr-1" /> Clear</Button>}
      </PageHead>
      <div className="flex-1 overflow-y-auto space-y-4 pr-1" data-testid="chat-messages">
        {msgs.length === 0 && (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="chat-suggestions">
            {SUGGESTIONS.map((s, i) => (
              <button key={s} onClick={() => send(s)} className="text-left bg-card border border-border rounded-sm p-4 text-sm hover:border-primary transition-colors" data-testid={`chat-suggestion-${i}`}>{s}</button>
            ))}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}-${i}`}>
            <div className={`max-w-[85%] rounded-sm px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-primary text-white" : "bg-card border border-border"}`}>
              {m.role === "assistant" && <Bot className="h-4 w-4 text-primary mb-1" />}
              {!m.content ? <span className="text-muted-foreground animate-pulse">Checking your shop data…</span>
                : m.role === "assistant" ? (
                  <div className="whitespace-normal [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_strong]:text-white [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_table]:text-xs [&_td]:pr-3 [&_th]:pr-3 [&_th]:text-left">
                    <ReactMarkdown>{m.content}</ReactMarkdown>
                  </div>
                ) : m.content}
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2 bg-card border border-border rounded-sm p-2 focus-within:border-primary transition-colors">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask about jobs, stock, revenue, or a repair…" className="flex-1 bg-transparent px-3 text-sm outline-none" data-testid="chat-input" />
        <Button type="submit" disabled={busy || !text.trim()} className="rounded-sm hover:bg-orange-600 hover:text-white" data-testid="chat-send-btn"><Send className="h-4 w-4" /></Button>
      </form>
    </div>
  );
}
